import { formatInvoiceStatus } from "../../i18n/formatters";
import { getLocale } from "../../i18n";
import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from "react-router-dom";
import axios from 'axios';
import Sidebar from '../common/sidebar';
import Footer from '../common/footer';

function formatCurrency(value, currency) {
  const safeCurrency = currency && String(currency).trim().length === 3 ? String(currency).trim().toUpperCase() : 'EUR';
  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: safeCurrency,
    minimumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(getLocale(), { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString(getLocale(), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatStatus(status) {
  return formatInvoiceStatus(status);
}

function getStatusClass(status) {
  switch (status) {
    case 'ISSUED': return 'iv-status iv-status--issued';
    case 'PAID': return 'iv-status iv-status--paid';
    case 'OVERDUE': return 'iv-status iv-status--overdue';
    case 'PARTIALLY_PAID': return 'iv-status iv-status--partial';
    case 'VOID': return 'iv-status iv-status--void';
    default: return 'iv-status iv-status--draft';
  }
}

function canEditInvoice(status) {
  return status === 'DRAFT' || status === 'ISSUED' || status === 'OVERDUE';
}

function canManagePayments(status) {
  return status === 'ISSUED' || status === 'PARTIALLY_PAID' || status === 'OVERDUE';
}

function getLifecycleMessage(status) {
  switch (status) {
    case 'ISSUED':
    case 'OVERDUE':
      return tr("Issued invoices can still be updated if you need to correct the draft before settlement. Paid and partially paid invoices remain locked.");
    case 'PARTIALLY_PAID':
      return tr("Partially paid invoices are locked to protect financial history. You can still record the remaining payments.");
    case 'PAID':
    case 'VOID':
      return tr("Paid and void invoices are locked to preserve financial history.");
    default:
      return tr("Draft invoices are fully editable until they are issued.");
  }
}

function getActivityActor(activity) {
  const user = activity?.user;
  if (!user) return 'System';
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return fullName || user.email || 'System';
}

export default function InvoiceDetailPage() {
  useTranslation();
  const auth = useSelector((state) => state.auth);
  const history = useNavigate();
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingPayment, setSavingPayment] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [error, setError] = useState('');
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'BANK_TRANSFER',
    reference: '',
    note: '',
  });

  const requestConfig = useMemo(() => ({
    headers: {
      Authorization: `Bearer ${auth.token}`,
      'Content-Type': 'application/json',
    },
  }), [auth.token]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [detailRes, settingsRes] = await Promise.all([
        axios.post('/api/invoice/details', JSON.stringify({
          workspace_id: auth.workspace_id,
          invoice_id: invoiceId,
        }), requestConfig),
        axios.get(`/api/invoice/settings?workspace_id=${auth.workspace_id}`, requestConfig),
      ]);
      setInvoice(detailRes.data.invoice);
      setSettings(settingsRes.data.settings || {});
    } catch (err) {
      setError('Failed to load invoice.');
    } finally {
      setLoading(false);
    }
  }, [auth.workspace_id, invoiceId, requestConfig]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totals = useMemo(() => {
    if (!invoice) return { subtotal: 0, tax: 0, total: 0 };
    return {
      subtotal: Number(invoice.subtotal || 0),
      tax: Number(invoice.tax_total || 0),
      total: Number(invoice.total || 0),
    };
  }, [invoice]);

  const balanceDue = invoice ? Math.max(0, totals.total - Number(invoice.amount_paid || 0)) : 0;

  const senderLines = useMemo(() => {
    if (!settings) return [];
    return [
      settings.legal_name,
      settings.address_line1,
      settings.address_line2,
      [settings.city, settings.postal_code].filter(Boolean).join(' '),
      settings.country,
      settings.sender_email,
      settings.sender_phone,
    ].filter(Boolean);
  }, [settings]);

  const clientLines = useMemo(() => {
    if (!invoice) return [];
    return [
      invoice.snapshot_company_name || invoice.snapshot_client_name,
      invoice.snapshot_billing_contact,
      invoice.snapshot_billing_email,
      invoice.snapshot_address,
      invoice.snapshot_vat_number ? `VAT: ${invoice.snapshot_vat_number}` : null,
      invoice.snapshot_tax_id ? `Tax ID: ${invoice.snapshot_tax_id}` : null,
    ].filter(Boolean);
  }, [invoice]);

  const isEditable = canEditInvoice(invoice?.status);
  const canRecordPayments = canManagePayments(invoice?.status);
  const canSendReminder = invoice
    && Number(invoice.balance_due || 0) > 0
    && !['PAID', 'VOID', 'DRAFT'].includes(invoice.status);
  const lastReminder = invoice?.reminders?.[0] || null;

  const updatePaymentField = (key, value) => {
    setPaymentForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleRecordPayment = async () => {
    if (!invoice) return;
    if (!paymentForm.amount || Number(paymentForm.amount) <= 0) {
      setError('Enter a payment amount greater than 0.');
      return;
    }

    setSavingPayment(true);
    setError('');
    try {
      await axios.post('/api/invoice/payments', JSON.stringify({
        invoice_id: invoice.id,
        workspace_id: auth.workspace_id,
        amount: paymentForm.amount,
        payment_date: paymentForm.payment_date,
        payment_method: paymentForm.payment_method,
        reference: paymentForm.reference,
        note: paymentForm.note,
        currency: invoice.currency,
      }), requestConfig);

      setPaymentForm({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'BANK_TRANSFER',
        reference: '',
        note: '',
      });

      loadData();
    } catch (err) {
      setError(err.response?.data?.errors?.[0]?.msg || 'Failed to record payment.');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!invoice) return;
    setSavingPayment(true);
    setError('');
    try {
      await axios.delete('/api/invoice/payments', {
        headers: requestConfig.headers,
        data: {
          payment_id: paymentId,
          workspace_id: auth.workspace_id,
        },
      });
      loadData();
    } catch (err) {
      setError(err.response?.data?.errors?.[0]?.msg || 'Failed to delete payment.');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleSendReminder = async () => {
    if (!invoice) return;

    setSendingReminder(true);
    setError('');
    try {
      await axios.post('/api/invoice/reminders/send', JSON.stringify({
        invoice_id: invoice.id,
        workspace_id: auth.workspace_id,
        reminder_type: 'OVERDUE',
      }), requestConfig);
      loadData();
    } catch (err) {
      const apiMessage = err.response?.data?.errors?.[0]?.msg;
      setError(apiMessage || 'Failed to send reminder.');
    } finally {
      setSendingReminder(false);
    }
  };

  const openDocument = (autoPrint = false) => {
    const search = autoPrint ? '?autoprint=1' : '';
    window.open(`/invoices/${invoiceId}/document${search}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div>
      <Sidebar />
      <div className='content-wrap' id='wrapper'>
        <div className='main'>
          <div className='ivd-shell'>
            <div className='ivd-topbar'>
              <button type='button' className='cp-back' onClick={() => history('/invoices')}>{tr("Back to Invoices")} </button>
              <div className='cp-actions'>
                <button type='button' className='cp-btn cp-btn--secondary' onClick={() => openDocument(false)}>{tr("Print Preview")} </button>
                <button type='button' className='cp-btn cp-btn--secondary' onClick={() => openDocument(true)}>{tr("Download PDF")} </button>
                {invoice && isEditable && (
                  <button type='button' className='cp-btn cp-btn--primary' onClick={() => history(`/invoices/${invoiceId}/edit`)}>{tr("Edit Invoice")} </button>
                )}
              </div>
            </div>

            {error && <div className='iv-error'>{error}</div>}

            {loading ? (
              <div className='cp-loading-card'>{tr("Loading invoice...")}</div>
            ) : invoice ? (
              <div className='ivd-layout'>
                <div className='ivd-doc'>
                  <div className='ivd-header'>
                    <div className='ivd-sender'>
                      {senderLines.length > 0 ? (
                        senderLines.map((line, i) => <p key={i}>{line}</p>)
                      ) : (
                        <p className='ivd-muted'>{tr("Configure your sender details in Invoice Settings.")}</p>
                      )}
                    </div>
                    <div className='ivd-header-right'>
                      <h1 className='ivd-title'>{tr("INVOICE")}</h1>
                      <div className='ivd-number'>{invoice.invoice_number}</div>
                      <span className={getStatusClass(invoice.status)}>{formatStatus(invoice.status)}</span>
                      <p className='ivd-locked-note'>{getLifecycleMessage(invoice.status)}</p>
                    </div>
                  </div>

                  <div className='ivd-meta-grid'>
                    <div className='ivd-bill-to'>
                      <div className='ivd-meta-label'>{tr("Bill To")}</div>
                      {clientLines.length > 0 ? (
                        clientLines.map((line, i) => <p key={i}>{line}</p>)
                      ) : (
                        <p className='ivd-muted'>{tr("No billing profile saved for this client.")}</p>
                      )}
                    </div>
                    <div className='ivd-dates'>
                      <div className='ivd-date-row'>
                        <span>{tr("Issue Date")}</span>
                        <strong>{formatDate(invoice.issue_date)}</strong>
                      </div>
                      <div className='ivd-date-row'>
                        <span>{tr("Due Date")}</span>
                        <strong>{formatDate(invoice.due_date)}</strong>
                      </div>
                      {invoice.service_period_start && (
                        <div className='ivd-date-row'>
                          <span>{tr("Service Period")}</span>
                          <strong>{formatDate(invoice.service_period_start)} – {formatDate(invoice.service_period_end)}</strong>
                        </div>
                      )}
                      <div className='ivd-date-row'>
                        <span>{tr("Currency")}</span>
                        <strong>{invoice.currency}</strong>
                      </div>
                    </div>
                  </div>

                  <table className='ivd-table'>
                    <thead>
                      <tr>
                        <th>{tr("Description")}</th>
                        <th className='ivd-th-num'>{tr("Qty")}</th>
                        <th className='ivd-th-num'>{tr("Unit")}</th>
                        <th className='ivd-th-num'>{tr("Unit Price")}</th>
                        <th className='ivd-th-num'>{tr("Tax %")}</th>
                        <th className='ivd-th-num'>{tr("Amount")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(invoice.lineItems || []).map((item) => (
                        <tr key={item.id}>
                          <td>{item.description}</td>
                          <td className='ivd-td-num'>{Number(item.quantity)}</td>
                          <td className='ivd-td-num'>{item.unit || tr("hrs")}</td>
                          <td className='ivd-td-num'>{formatCurrency(item.unit_price, invoice.currency)}</td>
                          <td className='ivd-td-num'>{Number(item.tax_rate)}%</td>
                          <td className='ivd-td-num'>{formatCurrency(item.line_total, invoice.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className='ivd-totals-wrap'>
                    <div className='ivd-totals'>
                      <div className='ivd-total-row'>
                        <span>{tr("Subtotal")}</span>
                        <strong>{formatCurrency(totals.subtotal, invoice.currency)}</strong>
                      </div>
                      <div className='ivd-total-row'>
                        <span>{tr("Tax")}</span>
                        <strong>{formatCurrency(totals.tax, invoice.currency)}</strong>
                      </div>
                      <div className='ivd-total-row ivd-total-grand'>
                        <span>{tr("Total")}</span>
                        <strong>{formatCurrency(totals.total, invoice.currency)}</strong>
                      </div>
                      {Number(invoice.amount_paid) > 0 && (
                        <div className='ivd-total-row'>
                          <span>{tr("Amount Paid")}</span>
                          <strong>{formatCurrency(invoice.amount_paid, invoice.currency)}</strong>
                        </div>
                      )}
                      <div className='ivd-total-row ivd-total-due'>
                        <span>{tr("Balance Due")}</span>
                        <strong>{formatCurrency(balanceDue, invoice.currency)}</strong>
                      </div>
                    </div>
                  </div>

                  <div className='ivd-payments-section'>
                    <div className='ivd-section-head'>
                      <h3>{tr("Payment History")}</h3>
                      <p>{tr("Track every payment applied to this invoice and keep the outstanding balance accurate.")}</p>
                    </div>

                    <div className='ivd-payment-grid'>
                      <div className='ivd-payment-card'>
                        <div className='ivd-payment-card-head'>
                          <h4>{tr("Record Payment")}</h4>
                          <span>{formatCurrency(balanceDue, invoice.currency)} {tr("outstanding")}</span>
                        </div>
                        {canRecordPayments ? (
                          <>
                            <div className='pm-grid pm-grid--2'>
                              <div className='pm-field'>
                                <label className='pm-label'>{tr("Amount")}</label>
                                <input className='pm-input' type='number' min='0' step='0.01' value={paymentForm.amount} onChange={(e) => updatePaymentField('amount', e.target.value)} />
                              </div>
                              <div className='pm-field'>
                                <label className='pm-label'>{tr("Payment date")}</label>
                                <input className='pm-input' type='date' value={paymentForm.payment_date} onChange={(e) => updatePaymentField('payment_date', e.target.value)} />
                              </div>
                              <div className='pm-field'>
                                <label className='pm-label'>{tr("Method")}</label>
                                <select className='pm-input' value={paymentForm.payment_method} onChange={(e) => updatePaymentField('payment_method', e.target.value)}>
                                  <option value='BANK_TRANSFER'>{tr("Bank transfer")}</option>
                                  <option value='CARD'>{tr("Card")}</option>
                                  <option value='CASH'>{tr("Cash")}</option>
                                  <option value='PAYPAL'>PayPal</option>
                                  <option value='OTHER'>{tr("Other")}</option>
                                </select>
                              </div>
                              <div className='pm-field'>
                                <label className='pm-label'>{tr("Reference")}</label>
                                <input className='pm-input' type='text' value={paymentForm.reference} onChange={(e) => updatePaymentField('reference', e.target.value)} placeholder={tr("Bank ref / transaction id")} />
                              </div>
                              <div className='pm-field pm-field--wide'>
                                <label className='pm-label'>{tr("Note")}</label>
                                <textarea className='pm-input pm-input--textarea' rows='3' value={paymentForm.note} onChange={(e) => updatePaymentField('note', e.target.value)} placeholder={tr("Optional internal payment note")} />
                              </div>
                            </div>
                            <div className='ivd-payment-actions'>
                              <button type='button' className='cp-btn cp-btn--primary' onClick={handleRecordPayment} disabled={savingPayment}>
                                {savingPayment ? tr("Saving...") : tr("Record Payment")}
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className='ivd-empty-note'>{tr("Payments can only be recorded after an invoice has been issued and before it becomes paid or void.")}</div>
                        )}
                      </div>

                      <div className='ivd-payment-card'>
                        <div className='ivd-payment-card-head'>
                          <h4>{tr("Ledger")}</h4>
                          <span>{tr("innerPayments", { count: (invoice.payments || []).length })}</span>
                        </div>
                        {(invoice.payments || []).length === 0 ? (
                          <div className='ivd-empty-note'>{tr("No payments have been recorded for this invoice yet.")}</div>
                        ) : (
                          <div className='ivd-payment-table-wrap'>
                            <table className='ivd-payment-table'>
                              <thead>
                                <tr>
                                  <th>{tr("Date")}</th>
                                  <th>{tr("Method")}</th>
                                  <th>{tr("Reference")}</th>
                                  <th>{tr("Amount")}</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {(invoice.payments || []).map((payment) => (
                                  <tr key={payment.id}>
                                    <td>{formatDate(payment.payment_date)}</td>
                                    <td>{String(payment.payment_method || tr("OTHER")).split('_').join(' ')}</td>
                                    <td>{payment.reference || '—'}</td>
                                    <td>{formatCurrency(payment.amount, payment.currency || invoice.currency)}</td>
                                    <td>
                                      {canRecordPayments && (
                                        <button type='button' className='ivd-payment-delete' onClick={() => handleDeletePayment(payment.id)} disabled={savingPayment}>{tr("Delete")} </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='ivd-activity-section'>
                    <div className='ivd-section-head'>
                      <h3>{tr("Activity Timeline")}</h3>
                      <p>{tr("Review the important invoice events in one clean audit trail.")}</p>
                    </div>

                    {(invoice.activities || []).length === 0 ? (
                      <div className='ivd-empty-note'>{tr("No activity has been recorded for this invoice yet.")}</div>
                    ) : (
                      <div className='ivd-activity-timeline'>
                        {(invoice.activities || []).map((activity) => (
                          <div key={activity.id} className='ivd-activity-item'>
                            <div className='ivd-activity-dot' />
                            <div className='ivd-activity-card'>
                              <div className='ivd-activity-top'>
                                <strong>{activity.message}</strong>
                                <span>{formatDateTime(activity.created_at)}</span>
                              </div>
                              <div className='ivd-activity-meta'>
                                <span>{getActivityActor(activity)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className='ivd-reminder-section'>
                    <div className='ivd-section-head'>
                      <h3>{tr("Reminder Workflow")}</h3>
                      <p>{tr("Send and review payment reminders without leaving the invoice record.")}</p>
                    </div>

                    <div className='ivd-reminder-card'>
                      <div className='ivd-reminder-head'>
                        <div>
                          <h4>{tr("Manual Reminder")}</h4>
                          <p>{tr("Recipient:")} <strong>{invoice.snapshot_billing_email || tr("No billing email saved")}</strong>
                          </p>
                        </div>
                        <button
                          type='button'
                          className='cp-btn cp-btn--secondary'
                          onClick={handleSendReminder}
                          disabled={!canSendReminder || sendingReminder}
                        >
                          {sendingReminder ? tr("Sending...") : tr("Send Reminder")}
                        </button>
                      </div>

                      {lastReminder ? (
                        <div className='ivd-reminder-summary'>
                          <div>
                            <span>{tr("Last reminder")}</span>
                            <strong>{formatDateTime(lastReminder.sent_at || lastReminder.created_at)}</strong>
                          </div>
                          <div>
                            <span>Status</span>
                            <strong>{formatStatus(lastReminder.status)}</strong>
                          </div>
                          <div>
                            <span>{tr("Attempts")}</span>
                            <strong>{lastReminder.attempts || 0}</strong>
                          </div>
                        </div>
                      ) : (
                        <div className='ivd-empty-note'>{tr("No reminders have been sent for this invoice yet.")}</div>
                      )}

                      {(invoice.reminders || []).length > 0 && (
                        <div className='ivd-reminder-list'>
                          {(invoice.reminders || []).map((reminder) => (
                            <div key={reminder.id} className='ivd-reminder-item'>
                              <div>
                                <strong>{formatStatus(reminder.reminder_type)} {tr("reminder")}</strong>
                                <span>{formatDateTime(reminder.sent_at || reminder.created_at)}</span>
                              </div>
                              <div>
                                <strong>{formatStatus(reminder.status)}</strong>
                                {reminder.last_error && <span>{reminder.last_error}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {(invoice.payment_instructions || invoice.notes) && (
                    <div className='ivd-notes-grid'>
                      {invoice.payment_instructions && (
                        <div className='ivd-notes-block'>
                          <div className='ivd-meta-label'>{tr("Payment Instructions")}</div>
                          <p>{invoice.payment_instructions}</p>
                        </div>
                      )}
                      {invoice.notes && (
                        <div className='ivd-notes-block'>
                          <div className='ivd-meta-label'>{tr("Notes")}</div>
                          <p>{invoice.notes}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {settings?.footer_notes && (
                    <div className='ivd-footer-notes'>{settings.footer_notes}</div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
